import { Coding, ValueSet, ValueSetCompose, ValueSetExpansionContains } from "../types/fhir";

export const getValueSetValues = (valueSet: ValueSet | undefined): Coding[] => {
  const codings: Coding[] = [];
  valueSet?.compose?.include.forEach((include) => {
    include?.concept?.forEach((concept) => {
      codings.push({
        code: concept.code,
        system: include.system,
        display: concept.display,
      });
    });
  });

  return codings;
};

/**
 * Group flat concepts by code system into a ValueSet.compose, which is where the builder reads codes from.
 */
export const composeFromConcepts = (concepts: { system: string; code: string; display?: string }[]): ValueSetCompose => {
  const includeBySystem = new Map<string, { code: string; display?: string }[]>();
  concepts.forEach((concept) => {
    const conceptsOfSystem = includeBySystem.get(concept.system) ?? [];
    conceptsOfSystem.push({ code: concept.code, display: concept.display });
    includeBySystem.set(concept.system, conceptsOfSystem);
  });

  return { include: [...includeBySystem.entries()].map(([system, concept]) => ({ system, concept })) };
};

const flattenExpansion = (contains: ValueSetExpansionContains[]): { system: string; code: string; display?: string }[] =>
  contains.flatMap((entry) => [
    // Entries without a code are placeholders in the hierarchy, not selectable codes
    ...(entry.system && entry.code ? [{ system: entry.system, code: entry.code, display: entry.display }] : []),
    ...flattenExpansion(entry.contains ?? []),
  ]);

/**
 * GET /api/ValueSet/getAllLatest returns ValueSets in summary mode ($expand'ed): the codes live in
 * expansion.contains and compose is absent. Everything downstream (enableWhen conditions, validation,
 * the questionnaire we export) reads codes from compose.include[].concept, so fill compose in from the
 * expansion.
 */
export const withComposeFromExpansion = (valueSet: ValueSet): ValueSet => {
  if (valueSet.compose?.include?.length) {
    return valueSet;
  }
  const contains = valueSet.expansion?.contains;
  if (!contains?.length) {
    return valueSet;
  }

  return { ...valueSet, compose: composeFromConcepts(flattenExpansion(contains)) };
};
